// =============================================================================
// PEDRA — src/components/CutListPanel.tsx
// Sequencia de cortes — a folha que o operador da serra segue.
//
// Vive no cartao "Cortes" da coluna da direita, entao a tabela e' densa:
// #, chapa, corte (posicao da lamina) e resultado. Peca-mae, comprimento e
// estagio de guilhotina continuam disponiveis no `title` de cada linha e na
// copia para a area de transferencia, que sai completa.
//
// Passar o mouse numa linha acende o corte correspondente no diagrama.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { CutChild, CutInstruction, OptimizationResult, Settings, SheetLayout } from '../core/types';
import { formatDimensions, formatLength } from '../core/units';
import { cutAxisLabel, formatCount } from '../lib/format';

export interface CutListPanelProps {
  result: OptimizationResult;
  settings: Settings;
  /** `null` = todas as chapas. */
  sheetIndex: number | null;
  /** Chapa em foco no diagrama: a linha correspondente fica destacada. */
  selectedIndex?: number;
  onHoverCut?(sheetIndex: number, seq: number | null): void;
  onSelectSheet?(index: number): void;
}

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

/** "1.200,0 mm" na coordenada onde a lamina comeca a remover material. */
function cutPosition(cut: CutInstruction, settings: Settings): string {
  return `${cut.axis} = ${formatLength(cut.pos, settings.unit, settings.decimalPlaces)}`;
}

function childText(child: CutChild, settings: Settings): string {
  const dims = formatDimensions(
    child.length,
    child.width,
    settings.unit,
    settings.decimalPlaces,
    settings.dimensionOrder,
  );
  return child.isWaste ? `${dims} (sobra)` : dims;
}

/** Copia texto para a area de transferencia, com plano B para navegadores antigos. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // cai no plano B
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// -----------------------------------------------------------------------------
// COMPONENTE
// -----------------------------------------------------------------------------

export function CutListPanel(props: CutListPanelProps): ReactElement {
  const { result, settings, sheetIndex, selectedIndex, onHoverCut, onSelectSheet } = props;
  const [sawMode, setSawMode] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const sheets: readonly SheetLayout[] = useMemo(
    () => (sheetIndex === null ? result.sheets : result.sheets.filter((s) => s.index === sheetIndex)),
    [result.sheets, sheetIndex],
  );

  /** Uma linha por corte, achatando todas as chapas: a coluna e' estreita. */
  const rows = useMemo(
    () => sheets.flatMap((s) => s.cuts.map((c) => ({ sheet: s, cut: c }))),
    [sheets],
  );

  const handleCopy = useCallback(() => {
    const lines: string[] = ['Chapa\t#\tPeça-mãe\tCorte\tComprimento\tEstágio\tResultado'];
    for (const s of sheets) {
      for (const c of s.cuts) {
        lines.push(
          [
            `Chapa ${s.index + 1}`,
            String(c.seq),
            formatDimensions(
              c.parent.length,
              c.parent.width,
              settings.unit,
              settings.decimalPlaces,
              settings.dimensionOrder,
            ),
            cutPosition(c, settings),
            formatLength(c.length, settings.unit, settings.decimalPlaces),
            String(c.stage),
            `${childText(c.childA, settings)} \\ ${childText(c.childB, settings)}`,
          ].join('\t'),
        );
      }
    }
    void copyText(lines.join('\n')).then((ok) => {
      setFeedback(ok ? 'Sequência copiada.' : 'Não foi possível copiar.');
      window.setTimeout(() => setFeedback(null), 4000);
    });
  }, [sheets, settings]);

  if (rows.length === 0) {
    return (
      <div className="cuts-root">
        <p className="cuts-empty empty-state">Nenhum corte a executar neste resultado.</p>
      </div>
    );
  }

  return (
    <div className={sawMode ? 'cuts-root is-saw' : 'cuts-root'}>
      <div className="cuts-toolbar no-print">
        <span className="cuts-count tabular">
          {`${formatCount(rows.length)} ${rows.length === 1 ? 'corte' : 'cortes'}`}
          {sheetIndex === null ? ` · ${formatCount(sheets.length)} plano(s)` : ''}
        </span>
        <div className="spacer" />
        <button
          type="button"
          className={sawMode ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
          onClick={() => setSawMode((v) => !v)}
          aria-pressed={sawMode}
          title="Aumenta a fonte e o contraste para leitura na máquina"
        >
          Modo serra
        </button>
        <button type="button" className="btn btn--sm" onClick={handleCopy}>
          Copiar
        </button>
      </div>

      {feedback ? (
        <p className="cuts-feedback" role="status">
          {feedback}
        </p>
      ) : null}

      <div className="cuts-scroll">
        <table className="table table--zebra cuts-table">
          <thead>
            <tr>
              <th scope="col" className="cuts-col-seq">
                #
              </th>
              <th scope="col" className="cuts-col-sheet">
                Chapa
              </th>
              <th scope="col">Corte</th>
              <th scope="col">Resultado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ sheet: s, cut: c }) => (
              <tr
                key={`${s.index}-${c.seq}`}
                className={s.index === selectedIndex ? 'cuts-row is-selected' : 'cuts-row'}
                onMouseEnter={onHoverCut ? () => onHoverCut(s.index, c.seq) : undefined}
                onMouseLeave={onHoverCut ? () => onHoverCut(s.index, null) : undefined}
                onClick={onSelectSheet ? () => onSelectSheet(s.index) : undefined}
                title={
                  `Peça-mãe ${formatDimensions(
                    c.parent.length,
                    c.parent.width,
                    settings.unit,
                    settings.decimalPlaces,
                    settings.dimensionOrder,
                  )} · corte de ${formatLength(
                    c.length,
                    settings.unit,
                    settings.decimalPlaces,
                  )} · estágio ${c.stage}`
                }
              >
                <td className="tabular cuts-col-seq">{c.seq}</td>
                <td className="tabular cuts-col-sheet">{s.index + 1}</td>
                <td className="tabular">
                  <span className={c.axis === 'x' ? 'cuts-axis is-x' : 'cuts-axis is-y'}>
                    {cutAxisLabel(c.axis)}
                  </span>{' '}
                  {cutPosition(c, settings)}
                </td>
                <td className="tabular cuts-result">
                  <span className={c.childA.isWaste ? 'cuts-child is-waste' : 'cuts-child'}>
                    {childText(c.childA, settings)}
                  </span>
                  <span className="cuts-sep" aria-hidden="true">
                    {' \\ '}
                  </span>
                  <span className={c.childB.isWaste ? 'cuts-child is-waste' : 'cuts-child'}>
                    {childText(c.childB, settings)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CutListPanel;
